

import { createTheme } from '@mui/material/styles'

// Modern Enterprise Palette
const palette = {
  light: {
    primary: {
      main: '#2563eb', // Vibrant Blue
      light: '#60a5fa',
      dark: '#1e40af',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed', // Modern Purple
      light: '#a78bfa',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f3f4f6', // Cool Gray 100
      paper: '#ffffff',
    },
    text: {
      primary: '#111827', // Gray 900
      secondary: '#4b5563', // Gray 600
      disabled: '#9ca3af', // Gray 400
    },
    divider: '#e5e7eb', // Gray 200
    action: {
      active: '#6b7280',
      hover: '#f3f4f6',
      selected: '#e5e7eb',
      disabled: '#9ca3af',
      disabledBackground: '#e5e7eb',
    },
  },
  dark: {
    primary: {
      main: '#3b82f6', // Bright Blue for Dark Mode
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6', // Bright Purple
      light: '#a78bfa',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0f172a', // Slate 900
      paper: '#1e293b', // Slate 800
    },
    text: {
      primary: '#f9fafb', // Gray 50
      secondary: '#9ca3af', // Gray 400
      disabled: '#6b7280', // Gray 500
    },
    divider: '#334155', // Slate 700
    action: {
      active: '#9ca3af',
      hover: '#1e293b',
      selected: '#334155',
      disabled: '#4b5563',
      disabledBackground: '#374151',
    },
  },
}

export const getTheme = (mode) => createTheme({
  palette: {
    mode,
    ...palette[mode],
    success: {
      main: '#10b981', // Emerald 500
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b', // Amber 500
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#000000',
    },
    error: {
      main: '#ef4444', // Red 500
      light: '#f87171',
      dark: '#b91c1c',
      contrastText: '#ffffff',
    },
    info: {
      main: '#3b82f6', // Blue 500
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.025em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '1.5rem', // Smaller on mobile
        fontWeight: 600,    // Lighter weight
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.025em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '1.125rem',
        fontWeight: 500,
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.025em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '1rem',
        fontWeight: 500,
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.015em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '0.9375rem', // ~15px
        fontWeight: 500,
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '-0.015em',
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '0.8125rem', // ~13px
        fontWeight: 500,
      },
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '0.8125rem', // ~13px
      },
    },
    body2: {
      fontSize: '0.75rem',
      lineHeight: 1.43,
      [createTheme({}).breakpoints.down('sm')]: {
        fontSize: '0.7rem', // ~11px
      },
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.8125rem', // Slightly smaller buttons
    },
  },
  shape: {
    borderRadius: 8, // Slightly tighter radius
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'dark' ? '#475569 #0f172a' : '#cbd5e1 #f3f4f6',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#475569' : '#cbd5e1',
            minHeight: 24,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: mode === 'dark' ? '#64748b' : '#94a3b8',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: mode === 'dark' ? '#64748b' : '#94a3b8',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: mode === 'dark' ? '#0f172a' : '#f3f4f6',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: mode === 'dark' ? '#0f172a' : '#f3f4f6',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small', // Default to small buttons
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          padding: '6px 12px',
          [createTheme({}).breakpoints.down('sm')]: {
            padding: '2px 8px', // Ultra compact on mobile
            fontSize: '0.7rem',
            minWidth: 'auto', // Allow buttons to shrink further
          },
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${palette[mode].primary.main} 0%, ${palette[mode].primary.dark} 100%)`,
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${palette[mode].secondary.main} 0%, ${palette[mode].secondary.dark} 100%)`,
        }
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small', // Default to small inputs
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: 'dense', // Default to dense spacing
        size: 'small',
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'small', // Default to compact tables
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette[mode].divider}`,
          padding: '6px 10px',
          [createTheme({}).breakpoints.down('sm')]: {
            padding: '4px 8px', // Tighter cells on mobile
          },
          fontSize: '0.8125rem',
        },
        head: {
          fontWeight: 600,
          backgroundColor: mode === 'light' ? '#f9fafb' : '#1e293b',
          color: palette[mode].text.secondary,
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          letterSpacing: '0.05em',
          padding: '8px 10px',
          [createTheme({}).breakpoints.down('sm')]: {
            padding: '6px 8px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td, &:last-child th': {
            border: 0,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'light'
            ? '0 2px 4px -1px rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.03)' // Subtler shadow
            : '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
          backgroundImage: 'none',
          border: `1px solid ${palette[mode].divider}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: mode === 'light'
            ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          paddingTop: 4,
          paddingBottom: 4
        }
      }
    }
  },
})

export default getTheme('light')
